import React from 'react';
import { PhysicalButton } from './PhysicalButton';

/**
 * TimeControlsModal Component
 *
 * Full-screen modal for match clock controls to reduce clutter in main broadcast layout.
 * Provides access to all time-related controls:
 * - Clock start/stop/reset
 * - Half selector (1st, 2nd, ET1, ET2)
 * - Injury time adjustments
 */
export const TimeControlsModal = ({
  isOpen,
  onClose,
  clock,
  onStartClock,
  onStopClock,
  onResetClock,
  onChangeHalf,
  onSetInjuryTime,
  match,
  onStartMatch,
  onEndMatch,
  onCancelMatch,
}) => {
  if (!isOpen) return null;

  const { minute = 0, second = 0, running = false, half = 1, injuryTime = 0 } = clock || {};
  const formattedTime = `${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-lg shadow-2xl border border-amber-600/30 max-w-2xl w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-t-lg">
          <h3 className="text-2xl font-bold text-amber-400 tracking-wide text-center">
            MATCH CLOCK CONTROLS
          </h3>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6">
          {/* Clock Display */}
          <div className="flex flex-col items-center gap-2">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
              CURRENT TIME
            </div>
            <div className="relative bg-black rounded-lg p-6 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)] border border-gray-800">
              {/* Ghost layer */}
              <div
                className="seven-segment text-8xl font-bold text-gray-800 tabular-nums leading-none"
                aria-hidden="true"
              >
                88:88
              </div>
              {/* Active layer */}
              <div className="absolute inset-0 p-6 seven-segment text-8xl font-bold text-amber-400 shadow-[0_0_30px_rgba(251,191,36,0.7)] tabular-nums leading-none">
                {formattedTime}
              </div>
            </div>
            {running && (
              <div className="flex items-center gap-2 text-green-400 animate-pulse">
                <div className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.8)]"></div>
                <span className="text-xs font-bold uppercase tracking-wide">Running</span>
              </div>
            )}
          </div>

          {/* Clock Control Buttons */}
          <div className="flex items-center justify-center gap-4">
            <PhysicalButton
              color="green"
              size="large"
              active={running}
              onClick={onStartClock}
            >
              START
            </PhysicalButton>
            <PhysicalButton
              color="red"
              size="large"
              onClick={onStopClock}
            >
              STOP
            </PhysicalButton>
            <PhysicalButton
              color="yellow"
              size="large"
              onClick={() => onResetClock(half)}
            >
              RESET
            </PhysicalButton>
          </div>

          {/* Half Selector */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest text-center">
              Match Half
            </div>
            <div className="flex items-center justify-center gap-2">
              {['1ST', '2ND', 'ET1', 'ET2'].map((label, idx) => (
                <PhysicalButton
                  key={label}
                  color="gray"
                  size="medium"
                  active={half === idx + 1}
                  onClick={() => onChangeHalf(idx + 1)}
                >
                  {label}
                </PhysicalButton>
              ))}
            </div>
          </div>

          {/* Injury Time Controls */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest text-center">
              Injury Time: <span className="text-amber-400 font-mono text-base">+{injuryTime}</span>
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {[1, 2, 3, 5].map((time) => (
                <PhysicalButton
                  key={time}
                  color="yellow"
                  size="small"
                  onClick={() => onSetInjuryTime(time)}
                >
                  +{time}
                </PhysicalButton>
              ))}
              <PhysicalButton
                color="gray"
                size="small"
                onClick={() => onSetInjuryTime(0)}
              >
                CLEAR
              </PhysicalButton>
            </div>
          </div>

          {/* Match Control Actions */}
          <div className="space-y-2 pt-4 border-t border-gray-700">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest text-center">
              Match Status
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {match?.status === 'scheduled' && (
                <PhysicalButton
                  color="green"
                  size="medium"
                  onClick={onStartMatch}
                >
                  Start Match
                </PhysicalButton>
              )}
              {match?.status === 'live' && (
                <PhysicalButton
                  color="red"
                  size="medium"
                  onClick={onEndMatch}
                >
                  End Match
                </PhysicalButton>
              )}
              <PhysicalButton
                color="red"
                size="medium"
                onClick={onCancelMatch}
              >
                Cancel Match
              </PhysicalButton>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
